/**
 * viewSettings — the office's Schedule View alert switches, read from the settings the store already holds.
 *
 * WHY THIS EXISTS (2026-10-30, plan §4). The icons on a Practice Ops card are Schedule View's alerts, filtered by
 * blockAlertKeys(payload, patNum, viewSettings, order): an alert shows only when the server RAN its rule (`ran`)
 * and the office has not switched it off (`viewSettings.alerts[key] !== false`). Schedule View gets `viewSettings`
 * from `/schedule-view/day` — which an office with Practice Pulse but not "Schedule - Chart Audit" is REFUSED
 * (403), so Practice Ops cannot use it. The server already runs only the rules the office enabled, so the
 * switches matter in the browser for exactly one alert: `task`, which is Firestore-only (the store's live task
 * listener) and so is never filtered server-side.
 *
 * So this reads the same blob the server resolves — the teamio.settings row `SCHEDULE_VIEW_SETTINGS` (a JSON
 * string), already in `customer.settings` — and returns the one shape blockAlertKeys needs: `{ alerts: {key: bool} }`.
 *
 * ⛔ ONE meaning of "absent". schedule-view-service lib/settings.js `resolveSettings` is THE resolver: an alert is ON
 * unless the blob says OFF, a missing / malformed / unreadable blob means every alert ON, and a boolean may arrive as
 * true/false, 'true'/'false', 'TRUE'/'FALSE', 1/0 or '1'/'0'. This file mirrors exactly that for `alerts`, and only
 * that — nothing else of the blob is read. cypress/node-tests/practice-ops-view-settings.test.js pins the two
 * together (it loads the sibling repo's resolver when it is checked out).
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

/** teamio.settings Property that holds the blob (same as schedule-view-service lib/settings.js). */
export const VIEW_SETTINGS_PROPERTY = 'SCHEDULE_VIEW_SETTINGS';

/** Same coercion as the service's `bool(value, fallback)`. */
function coerce(value, fallback) {
  if (value === true || value === false) return value;
  if (value === 'true' || value === 'TRUE' || value === 1 || value === '1') return true;
  if (value === 'false' || value === 'FALSE' || value === 0 || value === '0') return false;
  return fallback;
}

function blobFrom(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const row = list.find((r) => r && r.Property === VIEW_SETTINGS_PROPERTY);
  if (!row) return null;
  const stored = row.PropertyValue;
  if (stored && typeof stored === 'object') return Array.isArray(stored) ? null : stored;
  if (typeof stored !== 'string' || stored.trim() === '') return null;
  try {
    const parsed = JSON.parse(stored.trim());
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * @param {Array} settingsRows  customer.settings — [{ Property, PropertyValue }, ...]
 * @returns {{alerts: Object<string, boolean>}}  every alert the blob names, as a boolean (absent ⇒ ON by the rule below)
 */
export function officeViewSettings(settingsRows) {
  const blob = blobFrom(settingsRows);
  const alerts = {};
  const named = blob && blob.alerts && typeof blob.alerts === 'object' && !Array.isArray(blob.alerts) ? blob.alerts : {};
  Object.keys(named).forEach((key) => {
    alerts[key] = coerce(named[key], true);
  });
  return { alerts };
}

/** Is this alert switched ON? Absent ⇒ ON (the service's default for every alert). */
export function alertSwitchOn(viewSettings, key) {
  const alerts = viewSettings && viewSettings.alerts && typeof viewSettings.alerts === 'object' ? viewSettings.alerts : {};
  return alerts[key] !== false;
}
